/**
 * A group's published history — `/author/{kind}/{key}/versions` — for whoever
 * is authoring as the group: a platform admin or its Group Admin, never a
 * member (`docs/plans/group-accounts.md` §5). A member never sees a
 * version: publishing replaces what they have.
 *
 * **Both calls throw** (`api/journal.ts`'s split): a History that failed and
 * showed nothing reads as "never published", and a Restore that failed
 * silently leaves the author telling members about a version that is not live.
 */
import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = httpBase(import.meta.env.VITE_QV_SET_URL);

/** The admin route segment each publishable kind lives under. */
export type VersionedKind = "layouts" | "presets" | "chart-templates";

export interface VersionEntry {
  version: number;
  /** ISO, UTC. Null only for a row the server could not date. */
  publishedAt: string | null;
  /** Who pressed Publish — the admin or Group Admin, never the group account. */
  publishedBy: string;
  note: string | null;
}

const url = (kind: VersionedKind, key: string, rest = "") =>
  `${BASE}/author/${kind}/${encodeURIComponent(key)}/versions${rest}`;

export async function fetchVersions(kind: VersionedKind, key: string): Promise<VersionEntry[]> {
  if (!BASE) throw new Error("no server configured");
  const response = await fetch(url(kind, key), { headers: await requestHeaders() });
  if (!response.ok) throw new Error((await response.text()) || "history failed to load");
  return (await response.json()) as VersionEntry[];
}

/** Publish an old version again. Resolves to the version number it became. */
export async function restoreVersion(
  kind: VersionedKind,
  key: string,
  version: number,
): Promise<number | undefined> {
  if (!BASE) throw new Error("no server configured");
  const response = await fetch(url(kind, key, `/${version}/restore`), {
    method: "POST",
    headers: await requestHeaders(),
  });
  if (!response.ok) throw new Error((await response.text()) || "restore failed");
  const body = (await response.json()) as { version?: number };
  return typeof body.version === "number" ? body.version : undefined;
}

/**
 * Stop offering one of the group's items to members — parked server-side, so
 * the key and its grants survive and publishing brings it back.
 */
export async function withdrawPublished(kind: VersionedKind, key: string): Promise<void> {
  if (!BASE) throw new Error("no server configured");
  const response = await fetch(`${BASE}/author/${kind}/${encodeURIComponent(key)}/withdraw`, {
    method: "POST",
    headers: await requestHeaders(),
  });
  if (!response.ok) throw new Error((await response.text()) || "withdraw failed");
}
